import { logoBox } from '@/lib/logo-framing';
import { isStoredLogoSrc, useStoredLogoUrl } from '@/lib/use-logo-display-url';
import { cn } from '@/lib/utils';
import type { CompanyLogo } from '@mss_creator/shared';
import { type CSSProperties, useEffect } from 'react';

interface LogoImageProps {
  box: ReturnType<typeof logoBox>;
  onError: (() => void) | undefined;
}

function boxStyle(box: LogoImageProps['box']): CSSProperties {
  return {
    left: `${box.left}%`,
    top: `${box.top}%`,
    width: `${box.size}%`,
    height: `${box.size}%`,
  };
}

/**
 * The image in the square box the framing prescribes. `object-fit: contain`
 * fits it whole inside that box, whatever its proportions, so the drawing never
 * needs the image's own size.
 */
function BoxedImage({ url, box, onError }: LogoImageProps & { url: string }) {
  return (
    <img
      src={url}
      alt=""
      draggable={false}
      onError={onError}
      className="absolute max-w-none select-none object-contain"
      style={boxStyle(box)}
    />
  );
}

function StoredLogoImage({ src, box, onError }: LogoImageProps & { src: string }) {
  const { url, failed } = useStoredLogoUrl(src);
  useEffect(() => {
    // The image could not be fetched: the caller falls back as it does for a
    // broken external URL.
    if (failed) onError?.();
  }, [failed, onError]);
  return url ? <BoxedImage url={url} box={box} onError={onError} /> : null;
}

export interface CompanyLogoCircleProps {
  logo: CompanyLogo;
  /** What the circle is called to assistive technology (the company's name). */
  alt: string;
  /** Size and any treatment; defaults to the 64 px of the cards. */
  className?: string;
  /** The image could not be loaded: the caller shows its fallback instead. */
  onError?: () => void;
}

/**
 * A company's logo in its round frame (issue #20), at any size: the zoom and
 * position saved with the logo are percentages of the circle, so the card, the
 * settings field, the archives and the editor's previews all show the same
 * framing. White behind, and a hairline ring so a logo on a white ground still
 * reads as a circle.
 */
export function CompanyLogoCircle({ logo, alt, className, onError }: CompanyLogoCircleProps) {
  const box = logoBox(logo);
  return (
    <div
      role="img"
      aria-label={alt}
      className={cn('relative size-16 shrink-0 overflow-hidden rounded-full bg-white', className)}
    >
      {isStoredLogoSrc(logo.src) ? (
        <StoredLogoImage src={logo.src} box={box} onError={onError} />
      ) : (
        <BoxedImage url={logo.src} box={box} onError={onError} />
      )}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-gray-200 ring-inset"
      />
    </div>
  );
}
